import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ArrowsClockwise, Play, Pause, Broadcast, Compass, Info } from "@phosphor-icons/react";

export const EdgePatrolPanel = () => {
  const [cams, setCams] = useState([]);
  const [camId, setCamId] = useState("");
  const [positions, setPositions] = useState(4);
  const [dwell, setDwell] = useState(6);
  const [panSpeed, setPanSpeed] = useState(0.4);
  const [panMoveMs, setPanMoveMs] = useState(700);
  const [mode, setMode] = useState("sweep");
  const [tiltRows, setTiltRows] = useState(2);
  const [followTilt, setFollowTilt] = useState(true);
  const [sensitivity, setSensitivity] = useState(5);
  const [frame, setFrame] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/cameras");
    const edge = data.filter((c) => c.source === "edge" || c.ptz_movable);
    setCams(edge);
    setCamId((id) => id || (edge[0]?.id ?? ""));
  }, []);
  useEffect(() => { load(); }, [load]);

  const selected = cams.find((c) => c.id === camId);
  const running = !!selected?.ptz_patrol?.enabled;
  const activeMode = running ? (selected?.ptz_patrol?.mode || "sweep") : "off";

  useEffect(() => {
    const p = selected?.ptz_patrol;
    if (!p) return;
    if (p.enabled) setMode(p.mode || "sweep");
    if (p.positions) setPositions(p.positions);
    if (p.dwell_seconds) setDwell(p.dwell_seconds);
    if (p.pan_speed) setPanSpeed(p.pan_speed);
    if (p.pan_move_ms) setPanMoveMs(p.pan_move_ms);
    if (p.tilt_rows) setTiltRows(p.tilt_rows);
    if (typeof p.follow_tilt === "boolean") setFollowTilt(p.follow_tilt);
    if (p.sensitivity) setSensitivity(p.sensitivity);
  }, [camId, selected?.ptz_patrol]);

  useEffect(() => {
    if (!camId) return;
    const tick = () => api.get(`/cameras/${camId}/frame`).then((r) => setFrame(r.data)).catch(() => {});
    tick();
    pollRef.current = setInterval(tick, 4000);
    return () => clearInterval(pollRef.current);
  }, [camId]);

  const apply = async (nextMode) => {
    setBusy(true);
    try {
      if (nextMode === "off") {
        await api.post("/agent/ptz/stop", { camera_id: camId });
        toast.success("Patrol off", { description: "Camera holds its current view." });
      } else {
        await api.post("/agent/ptz/patrol", {
          camera_id: camId, mode: nextMode,
          positions, dwell_seconds: dwell, pan_speed: panSpeed, pan_move_ms: panMoveMs,
          tilt_rows: tiltRows, follow_tilt: followTilt, sensitivity,
        });
        toast.success(nextMode === "track" ? "Follow Motion armed" : "Sweep armed", {
          description: "Applies once the on-site agent picks it up (needs agent v4.2+).",
        });
      }
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update patrol"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-[#121214] border border-white/10 p-4" data-testid="edge-patrol-panel">
      <h2 className="font-display font-black uppercase tracking-tighter text-sm mb-1 flex items-center gap-2">
        <Compass size={16} weight="bold" className="text-[#32D74B]" /> Live PTZ Patrol
        <span className="font-mono-data text-[9px] px-1.5 py-0.5 bg-[#32D74B]/15 text-[#32D74B] border border-[#32D74B]/40">YOUR CAMERA</span>
      </h2>
      <p className="text-xs text-[#A1A1AA] leading-relaxed mb-4">
        Auto-rotate your real pan/tilt camera (e.g. Tapo C220) through the room, or let it <span className="text-[#32D74B]">follow motion</span> and track whoever's moving. The on-site Edge Agent moves the camera over ONVIF and the AI checks every angle.
      </p>

      {cams.length === 0 ? (
        <div className="flex items-start gap-2 bg-[#FF9F0A]/10 border border-[#FF9F0A]/40 p-3" data-testid="edge-patrol-empty">
          <Info size={16} weight="bold" className="text-[#FF9F0A] shrink-0 mt-0.5" />
          <p className="text-xs text-[#E4E4E7] leading-relaxed">
            No edge cameras connected yet. Connect your Tapo via the <span className="font-medium">Edge Agent</span> page first, then it will appear here for auto-patrol.
          </p>
        </div>
      ) : (
        <>
          <label className="block text-[10px] uppercase tracking-widest text-[#A1A1AA] mb-1.5">Edge Camera</label>
          <select data-testid="edge-patrol-camera-select" value={camId} onChange={(e) => setCamId(e.target.value)}
            className="w-full bg-[#1A1A1E] border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-[#32D74B] transition-colors mb-4">
            {cams.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>

          <div className="relative aspect-video bg-black border border-white/10 overflow-hidden mb-4" data-testid="edge-patrol-frame">
            {frame?.b64 ? (
              <img src={`data:image/jpeg;base64,${frame.b64}`} alt="live" className="w-full h-full object-cover opacity-90" />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-[#71717A] gap-2">
                <Broadcast size={32} weight="thin" />
                <span className="font-mono-data text-[10px]">Waiting for agent frame…</span>
              </div>
            )}
            {running && (
              <div className="absolute top-2 left-2 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-[#32D74B] live-dot" />
                <span className="font-mono-data text-[10px] tracking-widest">{activeMode === "track" ? "TRACKING MOTION" : "SWEEPING"}</span>
              </div>
            )}
          </div>

          <label className="block text-[10px] uppercase tracking-widest text-[#A1A1AA] mb-1.5">Patrol Mode</label>
          <div className="grid grid-cols-3 gap-1.5 mb-4" data-testid="edge-mode-switch">
            {[
              { key: "off", label: "Off" },
              { key: "sweep", label: "Sweep" },
              { key: "track", label: "Follow" },
            ].map((m) => (
              <button key={m.key} data-testid={`edge-mode-${m.key}`} disabled={busy || !camId}
                onClick={() => { setMode(m.key); apply(m.key); }}
                className={`py-2.5 text-xs font-display font-bold uppercase tracking-wide transition-colors disabled:opacity-50 border ${
                  activeMode === m.key
                    ? (m.key === "off" ? "bg-[#FF3B30] border-[#FF3B30] text-white" : "bg-[#32D74B] border-[#32D74B] text-black")
                    : "bg-[#1A1A1E] border-white/10 text-[#A1A1AA] hover:border-white/30"
                }`}>
                {m.label}
              </button>
            ))}
          </div>

          {mode === "track" ? (
            <>
              <p className="text-[11px] text-[#A1A1AA] leading-relaxed mb-3 flex items-start gap-1.5">
                <Info size={13} weight="bold" className="text-[#32D74B] shrink-0 mt-0.5" />
                The camera locks onto whoever is moving and follows them. When nothing moves for a while it simply holds its position.
              </p>
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1.5"><span className="text-xs uppercase tracking-widest text-[#A1A1AA]">Motion Sensitivity</span><span className="font-mono-data text-sm">{sensitivity}/10</span></div>
                <input type="range" min={1} max={10} value={sensitivity} onChange={(e) => setSensitivity(Number(e.target.value))} className="w-full accent-[#32D74B] cursor-pointer" data-testid="edge-sensitivity" />
              </div>
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1.5"><span className="text-xs uppercase tracking-widest text-[#A1A1AA]">Follow Speed</span><span className="font-mono-data text-sm">{panSpeed.toFixed(1)}</span></div>
                <input type="range" min={0.2} max={1} step={0.1} value={panSpeed} onChange={(e) => setPanSpeed(Number(e.target.value))} className="w-full accent-[#32D74B] cursor-pointer" data-testid="edge-follow-speed" />
              </div>
              <label className="flex items-center justify-between mb-4 cursor-pointer" data-testid="edge-follow-tilt">
                <span className="text-xs uppercase tracking-widest text-[#A1A1AA]">Also Tilt Up/Down</span>
                <input type="checkbox" checked={followTilt} onChange={(e) => setFollowTilt(e.target.checked)} className="w-4 h-4 accent-[#32D74B] cursor-pointer" />
              </label>
            </>
          ) : mode === "sweep" ? (
            <>
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1.5"><span className="text-xs uppercase tracking-widest text-[#A1A1AA]">Sweep Positions</span><span className="font-mono-data text-sm">{positions}</span></div>
                <input type="range" min={2} max={8} value={positions} onChange={(e) => setPositions(Number(e.target.value))} className="w-full accent-[#32D74B] cursor-pointer" data-testid="edge-positions" />
              </div>
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1.5"><span className="text-xs uppercase tracking-widest text-[#A1A1AA]">Pause At Each Stop</span><span className="font-mono-data text-sm">{dwell}s</span></div>
                <input type="range" min={3} max={20} value={dwell} onChange={(e) => setDwell(Number(e.target.value))} className="w-full accent-[#32D74B] cursor-pointer" data-testid="edge-dwell" />
              </div>
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1.5"><span className="text-xs uppercase tracking-widest text-[#A1A1AA]">Pan Speed</span><span className="font-mono-data text-sm">{panSpeed.toFixed(1)}</span></div>
                <input type="range" min={0.1} max={1} step={0.1} value={panSpeed} onChange={(e) => setPanSpeed(Number(e.target.value))} className="w-full accent-[#32D74B] cursor-pointer" data-testid="edge-pan-speed" />
              </div>
              <div className="mb-4">
                <div className="flex items-center justify-between mb-1.5"><span className="text-xs uppercase tracking-widest text-[#A1A1AA]">Pan Step (movement)</span><span className="font-mono-data text-sm">{panMoveMs}ms</span></div>
                <input type="range" min={200} max={2500} step={50} value={panMoveMs} onChange={(e) => setPanMoveMs(Number(e.target.value))} className="w-full accent-[#32D74B] cursor-pointer" data-testid="edge-pan-move" />
                <p className="text-[10px] text-[#71717A] mt-1 leading-relaxed">How far the camera turns at each stop. Bigger = wider jumps, fewer overlaps; smaller = finer steps.</p>
              </div>
              <div className="mb-4">
                <div className="flex items-center justify-between mb-1.5"><span className="text-xs uppercase tracking-widest text-[#A1A1AA]">Vertical Coverage</span><span className="font-mono-data text-sm">{tiltRows === 1 ? "Flat" : `${tiltRows} rows`}</span></div>
                <input type="range" min={1} max={3} value={tiltRows} onChange={(e) => setTiltRows(Number(e.target.value))} className="w-full accent-[#32D74B] cursor-pointer" data-testid="edge-tilt-rows" />
                <p className="text-[10px] text-[#71717A] mt-1 leading-relaxed">1 = pan side-to-side only. 2-3 = also tilt up/down between passes to cover floor-to-shelf height.</p>
              </div>
            </>
          ) : (
            <p className="text-[11px] text-[#71717A] leading-relaxed mb-4">Patrol is off — the camera holds its current view. Pick <span className="text-[#32D74B]">Sweep</span> to scan the room or <span className="text-[#32D74B]">Follow</span> to auto-track motion.</p>
          )}

          {activeMode !== mode && mode !== "off" && (
            <button data-testid="edge-apply-mode" onClick={() => apply(mode)} disabled={busy || !camId}
              className="w-full py-3 bg-[#32D74B] hover:bg-[#3ee45a] text-black font-display font-bold uppercase tracking-wide text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              <Play size={18} weight="fill" /> Apply {mode === "track" ? "Follow Motion" : "Sweep"}
            </button>
          )}
          <p data-testid="edge-patrol-status" className="text-[10px] text-[#71717A] font-mono-data mt-3 flex items-center gap-1.5">
            <ArrowsClockwise size={11} />
            {running
              ? (activeMode === "track"
                  ? `Following motion — sensitivity ${selected?.ptz_patrol?.sensitivity}/10${selected?.ptz_patrol?.follow_tilt ? ", pan+tilt" : ", pan only"}. Needs agent v4.3+.`
                  : `Sweeping ${selected?.ptz_patrol?.positions} stops, ${selected?.ptz_patrol?.dwell_seconds}s dwell${(selected?.ptz_patrol?.tilt_rows || 1) > 1 ? `, ${selected?.ptz_patrol?.tilt_rows} tilt rows` : ""}. Needs agent v4.3+.`)
              : "Idle. Frames still flow to Live Watch from the current view."}
          </p>
        </>
      )}
    </div>
  );
};
